import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShieldCheck, Zap, Download, Infinity as InfinityIcon, RefreshCw } from 'lucide-react';
type ModalType = 'terms' | 'privacy' | 'contact' | null;
function Modal({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return <AnimatePresence data-id="element-200">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose} data-id="element-201">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" data-id="element-202" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.25
      }} className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-203">
            <div className="flex items-center justify-between p-6 border-b border-brand-beige" data-id="element-204">
              <h3 className="text-xl md:text-2xl font-bold text-brand-green font-serif" data-id="element-205">
                {title}
              </h3>
              <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-full bg-brand-beige hover:bg-brand-beige/80 transition-colors" data-id="element-206">
                <X className="w-5 h-5 text-brand-text" data-id="element-207" />
              </button>
            </div>
            <div className="p-6 md:p-8 overflow-y-auto max-h-[calc(85vh-80px)] text-brand-text/80 text-sm md:text-base leading-relaxed space-y-4" data-id="element-208">
              {children}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-brand-green text-white relative overflow-hidden" data-id="element-209">
        {/* Decorative background */}
        <div className="absolute inset-0 overflow-hidden opacity-10 pointer-events-none" data-id="element-210">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] bg-brand-gold rounded-full blur-3xl" data-id="element-211"></div>
          <div className="absolute -bottom-32 -left-32 w-[400px] h-[400px] bg-brand-gold rounded-full blur-3xl" data-id="element-212"></div>
        </div>

        {/* Closing quote */}
        <div className="relative z-10 border-b border-white/10" data-id="element-213">
          <div className="max-w-4xl mx-auto px-6 py-12 md:py-14 text-center" data-id="element-214">
            <p className="font-serif text-xl sm:text-2xl md:text-3xl text-white/90 leading-snug italic tracking-tight" data-id="element-215">
              &ldquo;No es solo vender online. Es construir una{' '}
              <span className="text-brand-gold not-italic font-semibold" data-id="element-216">
                oportunidad real
              </span>{' '}
              para tu futuro.&rdquo;
            </p>
          </div>
        </div>

        {/* Main footer content */}
        <div className="relative z-10 max-w-6xl mx-auto px-6 py-16 md:py-20" data-id="element-217">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-10 text-center md:text-left" data-id="element-218">
            {/* Block 1: Brand */}
            <div data-id="element-219">
              <h4 className="font-serif text-lg font-bold tracking-wide mb-3 text-brand-gold" data-id="element-220">
                ECOSISTEMA ECOMMERCE
              </h4>
              <p className="text-white/55 text-sm leading-relaxed max-w-xs mx-auto md:mx-0" data-id="element-221">
                Aprende Ecommerce moderno y Amazon con sistemas prácticos
                diseñados para ayudarte a construir ingresos online reales.
              </p>
            </div>

            {/* Block 2: Legal links */}
            <div data-id="element-222">
              <h5 className="text-xs font-bold uppercase tracking-[0.18em] text-brand-gold mb-4" data-id="element-223">
                Legal
              </h5>
              <div className="flex flex-col gap-3" data-id="element-224">
                <button onClick={() => setActiveModal('terms')} className="text-white/60 text-sm hover:text-brand-gold transition-colors text-center md:text-left" data-id="element-225">
                  Términos y Condiciones
                </button>
                <button onClick={() => setActiveModal('privacy')} className="text-white/60 text-sm hover:text-brand-gold transition-colors text-center md:text-left" data-id="element-226">
                  Política de Privacidad
                </button>
              </div>
            </div>

            {/* Block 3: Trust */}
            <div data-id="element-227">
              <h5 className="text-xs font-bold uppercase tracking-[0.18em] text-brand-gold mb-4" data-id="element-228">
                Tu acceso
              </h5>
              <div className="flex flex-col gap-3" data-id="element-229">
                <span className="flex items-center justify-center md:justify-start gap-2 text-white/65 text-sm" data-id="element-230">
                  <ShieldCheck className="w-4 h-4 text-brand-gold flex-shrink-0" data-id="element-231" />
                  Compra Segura
                </span>
                <span className="flex items-center justify-center md:justify-start gap-2 text-white/65 text-sm" data-id="element-232">
                  <Zap className="w-4 h-4 text-brand-gold flex-shrink-0" data-id="element-233" />
                  Acceso Inmediato
                </span>
                <span className="flex items-center justify-center md:justify-start gap-2 text-white/65 text-sm" data-id="element-234">
                  <Download className="w-4 h-4 text-brand-gold flex-shrink-0" data-id="element-235" />
                  Descarga Digital
                </span>
                <span className="flex items-center justify-center md:justify-start gap-2 text-white/65 text-sm" data-id="element-236">
                  <InfinityIcon className="w-4 h-4 text-brand-gold flex-shrink-0" data-id="element-237" />
                  Acceso de por vida
                </span>
                <span className="flex items-center justify-center md:justify-start gap-2 text-white/65 text-sm" data-id="element-238">
                  <RefreshCw className="w-4 h-4 text-brand-gold flex-shrink-0" data-id="element-239" />
                  Actualizaciones gratuitas
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Digital products legal note */}
        <div className="relative z-10 border-t border-white/10" data-id="element-240">
          <div className="max-w-4xl mx-auto px-6 py-8 md:py-10" data-id="element-241">
            <div className="bg-white/5 border border-white/10 rounded-2xl px-6 py-6" data-id="element-242">
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-brand-gold mb-3 text-center" data-id="element-243">
                Política de productos digitales
              </p>
              <p className="text-white/55 text-xs sm:text-[13px] leading-relaxed mb-3" data-id="element-244">
                Al realizar esta compra, el cliente reconoce que está
                adquiriendo un producto digital descargable de acceso inmediato.
                Debido a la naturaleza intangible del contenido digital, todas
                las ventas son finales y no aplican devoluciones ni reembolsos
                una vez enviado el acceso al producto.
              </p>
              <p className="text-white/45 text-xs sm:text-[13px] leading-relaxed" data-id="element-245">
                Digital Products NC se compromete únicamente a garantizar la
                correcta entrega del contenido adquirido y la asistencia en caso
                de problemas técnicos relacionados con la descarga o recepción
                del material.
              </p>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="relative z-10 border-t border-white/10" data-id="element-246">
          <div className="max-w-6xl mx-auto px-6 py-6 text-center" data-id="element-247">
            {/* Copyright */}
            <p className="text-white/40 text-xs mb-3" data-id="element-248">
              © {new Date().getFullYear()} Ecosistema Ecommerce. Todos los
              derechos reservados.
            </p>
            {/* Disclaimer */}
            <p className="text-white/25 text-[10px] max-w-2xl mx-auto leading-relaxed" data-id="element-249">
              Este sitio no es parte del sitio web de Facebook o Facebook Inc.
              Además, este sitio NO está respaldado por Facebook de ninguna
              manera. FACEBOOK es una marca registrada de FACEBOOK, Inc.
            </p>
          </div>
        </div>
      </footer>

      {/* Terms Modal */}
      <Modal isOpen={activeModal === 'terms'} onClose={() => setActiveModal(null)} title="Términos y Condiciones" data-id="element-250">
        <p className="font-semibold text-brand-text" data-id="element-251">
          Última actualización: {new Date().getFullYear()}
        </p>
        <p data-id="element-252">
          Al adquirir cualquier producto digital ofrecido a través de este sitio
          web, aceptas los siguientes términos y condiciones:
        </p>
        <h4 className="font-bold text-brand-green mt-4" data-id="element-253">
          1. Naturaleza del Producto
        </h4>
        <p data-id="element-254">
          Todos los productos ofrecidos son de naturaleza digital (ebooks,
          plantillas, checklists). Una vez completada la compra, recibirás
          acceso inmediato al contenido a través de descarga digital.
        </p>
        <h4 className="font-bold text-brand-green mt-4" data-id="element-255">2. Uso Personal</h4>
        <p data-id="element-256">
          El contenido adquirido es para uso personal y educativo únicamente.
          Queda estrictamente prohibida la redistribución, reventa, copia o
          compartición del material con terceros sin autorización expresa por
          escrito.
        </p>
        <h4 className="font-bold text-brand-green mt-4" data-id="element-257">
          3. Política de No Reembolso
        </h4>
        <p data-id="element-258">
          Debido a la naturaleza digital e inmediata del producto, no se ofrecen
          reembolsos una vez que el contenido ha sido entregado y/o descargado.
          Al realizar la compra, aceptas esta condición.
        </p>
        <h4 className="font-bold text-brand-green mt-4" data-id="element-259">
          4. Propiedad Intelectual
        </h4>
        <p data-id="element-260">
          Todo el contenido, diseño, textos, imágenes y materiales están
          protegidos por derechos de autor. La propiedad intelectual pertenece
          exclusivamente a Sistema de Ingreso Desde Casa.
        </p>
        <h4 className="font-bold text-brand-green mt-4" data-id="element-261">5. Modificaciones</h4>
        <p data-id="element-262">
          Nos reservamos el derecho de modificar el contenido, precios, ofertas
          y estos términos en cualquier momento sin previo aviso. Las compras
          realizadas antes de cualquier cambio mantienen las condiciones
          vigentes al momento de la transacción.
        </p>
        <h4 className="font-bold text-brand-green mt-4" data-id="element-263">6. Resultados</h4>
        <p data-id="element-264">
          Los resultados pueden variar según la dedicación, esfuerzo y
          circunstancias individuales de cada persona. No garantizamos ingresos
          específicos ni resultados financieros.
        </p>
      </Modal>

      {/* Privacy Modal */}
      <Modal isOpen={activeModal === 'privacy'} onClose={() => setActiveModal(null)} title="Política de Privacidad" data-id="element-265">
        <p className="font-semibold text-brand-text" data-id="element-266">
          Última actualización: {new Date().getFullYear()}
        </p>
        <p data-id="element-267">
          Tu privacidad es importante para nosotros. Esta política describe cómo
          recopilamos, usamos y protegemos tu información personal.
        </p>
        <h4 className="font-bold text-brand-green mt-4" data-id="element-268">
          1. Información que Recopilamos
        </h4>
        <p data-id="element-269">
          Recopilamos información que proporcionas voluntariamente al realizar
          una compra, como tu nombre, dirección de correo electrónico e
          información de pago. Esta información es necesaria para procesar tu
          pedido y entregar el producto.
        </p>
        <h4 className="font-bold text-brand-green mt-4" data-id="element-270">
          2. Uso de la Información
        </h4>
        <p data-id="element-271">Tu información se utiliza exclusivamente para:</p>
        <ul className="list-disc pl-6 space-y-1" data-id="element-272">
          <li data-id="element-273">Procesar y entregar tu compra</li>
          <li data-id="element-274">Enviarte el producto adquirido</li>
          <li data-id="element-275">Comunicarnos contigo sobre tu pedido</li>
          <li data-id="element-276">
            Enviarte información relevante sobre actualizaciones del producto
            (si lo autorizas)
          </li>
        </ul>
        <h4 className="font-bold text-brand-green mt-4" data-id="element-277">
          3. Protección de Datos
        </h4>
        <p data-id="element-278">
          Implementamos medidas de seguridad técnicas y organizativas para
          proteger tu información personal contra acceso no autorizado, pérdida
          o alteración.
        </p>
        <h4 className="font-bold text-brand-green mt-4" data-id="element-279">
          4. No Venta de Datos
        </h4>
        <p data-id="element-280">
          No vendemos, alquilamos ni compartimos tu información personal con
          terceros con fines comerciales. Tu información es confidencial y está
          protegida.
        </p>
        <h4 className="font-bold text-brand-green mt-4" data-id="element-281">
          5. Cookies y Analíticas
        </h4>
        <p data-id="element-282">
          Este sitio puede utilizar cookies y herramientas de analítica web para
          mejorar la experiencia del usuario y analizar el tráfico. Estas
          herramientas recopilan información de forma anónima.
        </p>
        <h4 className="font-bold text-brand-green mt-4" data-id="element-283">6. Tus Derechos</h4>
        <p data-id="element-284">
          Tienes derecho a solicitar acceso, corrección o eliminación de tus
          datos personales en cualquier momento contactándonos a través de
          nuestro correo de soporte.
        </p>
      </Modal>

      {/* Contact Modal */}
      <Modal isOpen={activeModal === 'contact'} onClose={() => setActiveModal(null)} title="Contacto" data-id="element-285">
        <p className="text-lg text-brand-text" data-id="element-286">
          Si tienes dudas, preguntas o necesitas soporte con tu compra, puedes
          escribirnos directamente.
        </p>
        <div className="bg-brand-beige/50 rounded-xl p-6 space-y-4 mt-4" data-id="element-287">
          <div data-id="element-288">
            <p className="text-xs font-semibold uppercase tracking-wider text-brand-text/50 mb-1" data-id="element-289">
              Correo electrónico
            </p>
            <p className="text-lg font-semibold text-brand-green" data-id="element-290">
              productodigitalnc@gmail.com
            </p>
          </div>
          <div data-id="element-291">
            <p className="text-xs font-semibold uppercase tracking-wider text-brand-text/50 mb-1" data-id="element-292">
              Horario de atención
            </p>
            <p className="text-brand-text font-medium" data-id="element-293">
              Lunes a Viernes | 9:00 AM - 6:00 PM EST
            </p>
          </div>
          <div data-id="element-294">
            <p className="text-xs font-semibold uppercase tracking-wider text-brand-text/50 mb-1" data-id="element-295">
              Tiempo estimado de respuesta
            </p>
            <p className="text-brand-text font-medium" data-id="element-296">24 - 48 horas hábiles</p>
          </div>
        </div>
        <p className="text-brand-text/60 text-sm mt-4" data-id="element-297">
          Estamos aquí para ayudarte. Tu satisfacción es nuestra prioridad.
        </p>
      </Modal>
    </>;
}